// src/hooks/useCurrentBearing.js

import { useState, useEffect, useRef } from "react";

const LERP_FACTOR = 0.1; // Controls the speed of the rotation. Smaller is smoother/slower.

/**
 * A custom hook that smoothly animates a bearing value towards a target.
 * It handles shortest-path rotation (e.g., from 350deg to 10deg).
 * @param {number} targetBearing The desired final bearing (0-360).
 * @returns {number} The current, smoothly interpolated bearing for display.
 */
export const useCurrentBearing = targetBearing => {
    const [currentBearing, setCurrentBearing] = useState(targetBearing);
    const animationFrameRef = useRef();

    useEffect(() => {
        const animate = () => {
            setCurrentBearing(prevBearing => {
                // Find the difference and normalize it for the shortest path
                let diff = targetBearing - prevBearing;
                if (diff > 180) {
                    diff -= 360;
                } else if (diff < -180) {
                    diff += 360;
                }

                // If the difference is very small, snap to the target and stop animating
                if (Math.abs(diff) < 0.1) {
                    cancelAnimationFrame(animationFrameRef.current);
                    return targetBearing;
                }

                // Apply linear interpolation (LERP) for smooth animation
                const nextBearing = prevBearing + diff * LERP_FACTOR;

                // Keep the animation going
                animationFrameRef.current = requestAnimationFrame(animate);

                // Normalize the result to stay within 0-360
                return (nextBearing + 360) % 360;
            });
        };

        // Start the animation whenever the target bearing changes
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = requestAnimationFrame(animate);

        // Cleanup function to cancel animation when the component unmounts
        return () => cancelAnimationFrame(animationFrameRef.current);
    }, [targetBearing]); // This effect re-runs ONLY when the targetBearing changes

    return currentBearing;
};
